import React from 'react';
import { clsx } from 'clsx';
import { RangeSlider } from '../UI';

// #region Segmented
/** A row of mutually exclusive choices. Wraps on narrow screens rather than scrolling. */
export function Segmented<T extends string>({ label, value, options, onChange }: {
    label: string;
    value: T;
    options: { value: T; title: string; hint?: string }[];
    onChange: (value: T) => void;
}): React.ReactElement {
    return (
        <fieldset>
            <legend className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{label}</legend>
            <div className="mt-2 grid grid-cols-3 gap-2">
                {options.map((option) => {
                    const active = option.value === value;
                    return (
                        <button
                            key={option.value}
                            type="button"
                            aria-pressed={active}
                            onClick={() => onChange(option.value)}
                            className={clsx(
                                'rounded-xl border px-2 py-2 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-raised sm:px-3',
                                active
                                    ? 'border-blue-600 bg-blue-600/10 text-blue-900 shadow-sm dark:border-blue-400 dark:bg-blue-500/15 dark:text-blue-100'
                                    : 'border-surface-border bg-surface text-zinc-700 hover:border-surface-border-strong dark:text-zinc-300'
                            )}
                        >
                            <span className="block text-[13px] font-semibold leading-tight sm:text-sm">{option.title}</span>
                            {option.hint ? <span className="mt-0.5 block text-xs tabular-nums text-zinc-500 dark:text-zinc-400">{option.hint}</span> : null}
                        </button>
                    );
                })}
            </div>
        </fieldset>
    );
}

// #region Slider
/** A labelled range input with its current value shown beside the label. */
export const Slider: React.FunctionComponent<{
    id: string;
    label: string;
    display: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    onChange: (value: number) => void;
    hint?: React.ReactNode;
}> = ({ id, label, display, value, min, max, step = 1, onChange, hint }): React.ReactElement => (
    <div>
        <div className="flex items-baseline justify-between gap-3">
            <label htmlFor={id} className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{label}</label>
            <span className="rounded-md bg-surface-muted px-2 py-0.5 text-sm font-semibold tabular-nums text-zinc-900 dark:text-zinc-100" aria-hidden>{display}</span>
        </div>
        <div className="mt-3">
            <RangeSlider
                type="range"
                value={String(value)}
                min={String(min)}
                max={String(max)}
                step={step}
                onChange={(e) => onChange(Number(e.target.value))}
                // RangeSlider spreads rangeProps last, so this className replaces its default one.
                rangeProps={{ id, className: 'block w-full h-2 cursor-pointer accent-blue-600 dark:[color-scheme:dark]', 'aria-valuetext': display }}
            />
        </div>
        {hint ? <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">{hint}</p> : null}
    </div>
);
